import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import compose from 'recompose/compose';
import withState from 'recompose/withState';
import withHandlers from 'recompose/withHandlers';
import mapProps from 'recompose/mapProps';
import defaultProps from 'recompose/defaultProps';

import assoc from 'ramda/es/assoc';
import has from 'ramda/es/has';
import both from 'ramda/es/both';
import equals from 'ramda/es/equals';
import always from 'ramda/es/always';
import length from 'ramda/es/length';
import cond from 'ramda/es/cond';
import complement from 'ramda/es/complement';
import trim from 'ramda/es/trim';
import T from 'ramda/es/T';

import { addChild } from 'app/actions/addChild';
import { checkChild, ACCOUNT_IS_CARDNUM } from 'app/actions/addChild/check';
import { popup } from 'app/common/actions/popup';


export const LAST_STEP = 3;

const selectSubmitTitle = createSelector(
  [
    ({ sending }) => sending,
    ({ step }) => step,
  ],
  (sending, step) => (sending
    ? cond([
      [equals(LAST_STEP), always('Добавление...')],
      [T, always('Проверка...')],
    ])(step)
    : cond([
      [equals(LAST_STEP), always('Добавить ребенка')],
      [T, always('Проверить')],
    ])(step)
  )
);

const validCardNum = value => () => {
  const len = length(value);
  return (len === 12 || len === 13);
};
const validAccountNum = value => () => !!(length(value));
const valEmptyEither = (value1, value2) => () => !(length(value1) && length(value2));

const selectSubmitTip = createSelector(
  [
    ({ step }) => step,
    ({ values }) => values,
  ],
  (step, values) => cond([
    [
      both(equals(1), complement(validCardNum(values.card_number))),
      always('Заполните номер карты, 12-13 цифр'),
    ], [
      both(equals(2), complement(validAccountNum(values.account_number))),
      always('Заполните номер лицевого счета'),
    ], [
      both(equals(3), valEmptyEither(values.first_name, values.birthdate)),
      always('Заполните имя и дату рождения ребенка'),
    ],
  ])(step)
);

const defaultState = {
  first_name: '',
  account_number: '',
  birthdate: '',
  card_number: '',
};

const fieldsNames = {
  first_name: 'Имя ребенка',
  account_number: 'Лицевой счет',
  birthdate: 'Дата рождения',
  card_number: 'Номер идентификатора',
};

export default compose(
  connect(),

  defaultProps({
    fieldsNames,
  }),

  withState('values', 'setValues', defaultState),
  withState('error', 'setError', ''),
  withState('sending', 'setSending', false),
  withState('step', 'setStep', 1),

  withHandlers({
    handleChange: ({ setValues, setError, step }) => (e) => {
      const { name, value } = e.currentTarget;
      if (has(name, defaultState)) {
        setError('');
        setValues(step === 1
          // обнуляем все кроме номера карты, на первом шагу,
          // так как при 12-значном значении устанавливается лицевой счет в номер карты
          ? assoc(name, value, defaultState)
          : assoc(name, value));
      }
    },
    disabledDays: () => day => (Date.now() - day < 0),
    handleChangeDate: ({ setValues, setError }) => (value, validType, name) => {
      if (validType === 'valid' && has(name, defaultState)) {
        setError('');
        setValues(assoc(name, value));
      }
    },
    handleSubmit: ({
      dispatch,
      setSending,
      values,
      onClose,
      setError,
      setValues,
      step,
      setStep,
    }) => async (e) => {
      e.preventDefault();
      setSending(true);

      const needSkipAccount = (step === 1
        && length(trim(values.card_number)) === ACCOUNT_IS_CARDNUM);
      const currentValues = (needSkipAccount
        ? assoc('account_number', values.card_number, values)
        : values);

      if (needSkipAccount) {
        setValues(currentValues);
      }
      const res = (step === LAST_STEP
        ? await dispatch(addChild(values))
        : await dispatch(checkChild(currentValues)));

      setSending(false);

      if (res) {
        setError(res);
        return;
      }
      if (step === LAST_STEP) {
        dispatch(popup('Ребенок успешно добавлен'));
        onClose();
        return;
      }
      setStep(st => (needSkipAccount ? st + 2 : st + 1));
    },
  }),

  mapProps(props => ({
    ...props,
    submitTip: selectSubmitTip(props),
    submitTitle: selectSubmitTitle(props),
  }))
);
